import React, { useEffect, useState } from 'react';
import Footer from '../../components/Footer';
import Header from '../../components/Header';
import moment from 'moment';
import Swal from 'sweetalert2';
import { generateAxiosInstanceCommon } from '../../hooks/useAuthorizedReuestCommon';
import { useNavigate } from 'react-router-dom';
import { GeneratePdf } from '../../components/GeneratePdf';
import { sectionTeacherPerformanceFormValidation } from '../../validation/Validation';
import { ZodError } from 'zod';
import { ErrorMessage } from '../../validation/validationError';
import { Loader } from '../../components/ModalsCommonFunctions';
import { findPageNumber, searchArray, SortIcon } from '../../components/Navigation';
import Pagination from '../../components/Pagination';

const SectionTeacherPerformance = () => {
    const authorizedAxiosCommon = generateAxiosInstanceCommon();
    let instituteMasterId = localStorage.getItem("instituteMasterId")
    let sectionMasterId = localStorage.getItem("sectionMasterId")

    const initialFormState = {
        academicYearMasterId: '',
        startDate: '',
        endDate: ''
    }
    const [sectionData, setSectionData] = useState(initialFormState)
    const [teacherList, setTeacherList] = useState([])
    const [instituteMasterDetails, setInstituteMasterDetails] = useState({})
    const [sectionMasterDetails, setSectionMasterDetails] = useState({})
    const [academicYearList, setAcademicYearList] = useState([]);
    const [errors, setErrors] = useState([]);

    const [startIndex, setStartIndex] = useState(null);
    const [currPage, setCurrPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [displayedData, setDisplayedData] = useState([]);
    const [totalPages, setTotalPages] = useState(0);
    const [saveId, setSaveId] = useState(null);

    const [teachingCountSort, setTeachingCountSort] = useState(null);
    const [assignmentCountSort, setAssignmentCountSort] = useState(null);
    const [examCountSort, setExamCountSort] = useState(null);
    const [completionPercentageSort, setCompletionPercentageSort] = useState(null);
    const [ratingSort, setRatingSort] = useState(null);

    const [searchValue, setSearchValue] = useState(null);
    const searchKeys = ["teacherName", "teacherMappingDetails.instituteGradeMasterDetails.gradeName", "teacherMappingDetails.instituteDivisionMasterDetails.divisionName", "teacherMappingDetails.instituteSubjectMasterDetails.subjectName", "dailyTeachingCount", "dailyAssignmentCount", "totalExamsCount", "completionPercentage", "ratingAverage"];

    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const handleNavigate = (route) => {
        navigate(route);
    };

    useEffect(() => {
        getAcademicYearList();
    }, []);

    useEffect(() => {
        if (searchValue === '' && searchValue !== null) {
            setCurrPage(1);
            getTeacherPerformance();
        }
    }, [searchValue]);

    const handleEmptySearch = (e) => {
        setSearchValue(e.target.value);
    }

    const handleSearch = async () => {
        if (searchValue !== null) {
            const apiUrl = `daily-teaching-count-report?instituteMasterId=${instituteMasterId}&sectionMasterId=${sectionMasterId}&startDate=${sectionData.startDate}&endDate=${sectionData.endDate}`;
            const data = await searchArray("C", apiUrl, searchKeys, searchValue.trim(), "data")
            setTeacherList(data)
        }
    }

    useEffect(() => {
        if (saveId) {
            const pageNumber = findPageNumber(teacherList, saveId, itemsPerPage);
            setCurrPage(pageNumber);
        }
    }, [saveId])

    useEffect(() => {
        setTotalPages(Math.ceil(teacherList.length / itemsPerPage));
        const startIndex = (currPage - 1) * itemsPerPage;
        let endIndex = startIndex + itemsPerPage;

        if (endIndex > teacherList.length) {
            endIndex = teacherList.length;
        }
        setStartIndex(startIndex);
        setDisplayedData(teacherList.slice(startIndex, endIndex));
    }, [teacherList, currPage, itemsPerPage]);

    const getAcademicYearList = () => {
        const apiUrlForSyllabus = `/sections-academic-years?sectionMasterId=${sectionMasterId}`;
        authorizedAxiosCommon && authorizedAxiosCommon.get(apiUrlForSyllabus)
            .then(response => {
                setAcademicYearList(response.data.data)
            })
            .catch(error => {
                const errorMsg = error?.response?.data?.message || 'Something went wrong!';
                console.error('Error fetching data:', errorMsg);
            });
    }

    const selectedYear = academicYearList.find(
        year => year.id == sectionData.academicYearMasterId
    );

    const minDate = selectedYear ? selectedYear.startDate : '';
    const maxDate = selectedYear ? selectedYear.endDate : '';

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setSectionData(prevState => ({
            ...prevState,
            [name]: value
        }));

        if (value.trim().length !== 0) {
            setErrors(prevErrors => prevErrors.filter(error => error.path[0] !== name));
        }
    }

    const getTeacherPerformance = async () => {
        try {
            await sectionTeacherPerformanceFormValidation.parseAsync(sectionData);

            let newErrors = [];
            for (const [key, value] of Object.entries(sectionData)) {
                if (key === "startDate" || key === "endDate") {
                    const date1 = new Date(sectionData.startDate);
                    const date2 = new Date(sectionData.endDate);

                    if (date1.getTime() > date2.getTime()) {
                        const errorIndexStartDate = newErrors.findIndex(err => err.path.includes("startDate"));
                        const errorIndexEndDate = newErrors.findIndex(err => err.path.includes("endDate"));
                        const errorMessage = "End date cannot be earlier than the start date";

                        if (errorIndexStartDate !== -1 || errorIndexEndDate !== -1) {
                            newErrors[errorIndexStartDate].message = errorMessage;
                            newErrors[errorIndexEndDate].message = errorMessage;
                        } else {
                            newErrors.push({ path: ["startDate"], message: errorMessage });
                            newErrors.push({ path: ["endDate"], message: errorMessage });
                        }
                    }
                }
            }

            if (newErrors.length > 0) {
                setErrors(newErrors);
                return;
            } else {
                setErrors([]);
            }

            if (sectionData.academicYearMasterId.length !== 0) {
                try {
                    setLoading(true);
                    const apiUrl = `daily-teaching-count-report?instituteMasterId=${instituteMasterId}&sectionMasterId=${sectionMasterId}&startDate=${sectionData.startDate}&endDate=${sectionData.endDate}`;
                    const response = await authorizedAxiosCommon.get(apiUrl);
                    // setTeacherList(response.data);

                    setTeacherList(response.data.data);
                    setInstituteMasterDetails(response.data.instituteMasterDetails)
                    setSectionMasterDetails(response.data.sectionMasterDetails)

                    setTeachingCountSort(null);
                    setAssignmentCountSort(null);
                    setExamCountSort(null);
                    setCompletionPercentageSort(null);
                    setRatingSort(null);
                } catch (error) {
                    const errorMsg = error?.response?.data?.message || 'Something went wrong!';
                    Swal.fire(
                        { text: errorMsg }
                    );
                } finally {
                    setLoading(false);
                }
            }
        } catch (error) {
            if (error instanceof ZodError) {
                let newErrors = [...error.errors];
                for (const [key, value] of Object.entries(sectionData)) {
                    if (key === "startDate" || key === "endDate") {
                        const date1 = new Date(sectionData.startDate);
                        const date2 = new Date(sectionData.endDate);

                        if (date1.getTime() > date2.getTime()) {
                            const errorIndexStartDate = newErrors.findIndex(err => err.path.includes("startDate"));
                            const errorIndexEndDate = newErrors.findIndex(err => err.path.includes("endDate"));
                            const errorMessage = "End date cannot be earlier than the start date";

                            if (errorIndexStartDate !== -1 || errorIndexEndDate !== -1) {
                                newErrors[errorIndexStartDate].message = errorMessage;
                                newErrors[errorIndexEndDate].message = errorMessage;
                            } else {
                                newErrors.push({ path: ["startDate"], message: errorMessage });
                                newErrors.push({ path: ["endDate"], message: errorMessage });
                            }
                        }
                    }
                }
                setErrors(newErrors);
            }
        }
    }

    const handleInputReset = () => {
        setSectionData(initialFormState);
        setTeacherList([]);
        setErrors([]);
    }

    useEffect(() => {
        if (teachingCountSort == true || teachingCountSort == false) {
            setAssignmentCountSort(null);
            setExamCountSort(null);
            setCompletionPercentageSort(null);
            setRatingSort(null);
        }
    }, [teachingCountSort])

    useEffect(() => {
        if (assignmentCountSort == true || assignmentCountSort == false) {
            setTeachingCountSort(null);
            setExamCountSort(null);
            setCompletionPercentageSort(null);
            setRatingSort(null);
        }
    }, [assignmentCountSort])

    useEffect(() => {
        if (examCountSort == true || examCountSort == false) {
            setTeachingCountSort(null);
            setAssignmentCountSort(null);
            setCompletionPercentageSort(null);
            setRatingSort(null);
        }
    }, [examCountSort])

    useEffect(() => {
        if (completionPercentageSort == true || completionPercentageSort == false) {
            setTeachingCountSort(null);
            setAssignmentCountSort(null);
            setExamCountSort(null);
            setRatingSort(null);
        }
    }, [completionPercentageSort])

    useEffect(() => {
        if (ratingSort == true || ratingSort == false) {
            setTeachingCountSort(null);
            setAssignmentCountSort(null);
            setExamCountSort(null);
            setCompletionPercentageSort(null);
        }
    }, [ratingSort])

    return (
        <>
            <Loader loading={loading} />
            <div className="wrapper d-flex flex-column min-vh-100 bg-light">
                <div id='scroll-up'></div>
                <Header />
                <nav aria-label="breadcrumb">
                    <ol className="breadcrumb my-0 ms-2">
                        <li className="breadcrumb-item dashboard" onClick={() => handleNavigate('/section-dashboard')}>
                            <span>Dashboard</span>
                        </li>
                        <li className="breadcrumb-item active"><span>Teacher Performance</span></li>
                    </ol>
                </nav>
                <div className="container-lg">
                    <div className="row">
                        <div className="card border-info mx-auto mt-3">
                            <div className="card-content collpase show">
                                <div className="card-header" style={{ backgroundColor: '#3081D0', borderRadius: 0 }}>
                                    <h4 className="text-white"><b><span className="fa fa-file-o"></span><i class="fas fa-chart-line"></i>Teacher Performance</b></h4>
                                </div>
                                <div className="card-body ">
                                    <div className="row">
                                        <div className="col-md-12">
                                            <div className="row">
                                                <div className="col-md-6">
                                                    <label for="academicYearMasterId" className="col-form-label"><span style={{ color: "red" }}>*</span>Select Academic Year:</label>
                                                    <select class="form-select" aria-label="Default select example" name='academicYearMasterId' onChange={handleInputChange} value={sectionData.academicYearMasterId}>
                                                        <option value={''} disabled>Select Year</option>
                                                        {
                                                            academicYearList?.map((year) => {
                                                                return <option value={year.id}>{year.academicYearMasterDetails.yearName}</option>
                                                            })
                                                        }
                                                    </select>
                                                    <ErrorMessage errors={errors} field="academicYearMasterId" />
                                                </div>
                                                {
                                                    sectionData.academicYearMasterId.length !== 0 && <>

                                                        <div className="col-md-6">
                                                            <label for="startDate" className="col-form-label">From Date: (Min-Max: {moment(minDate).format('DD-MM-YYYY')} - {moment(maxDate).format('DD-MM-YYYY')})</label>
                                                            <input type="date" className="form-control" id="startDate" name='startDate' onChange={handleInputChange} value={sectionData.startDate} min={minDate} max={maxDate} />
                                                            <ErrorMessage errors={errors} field="startDate" />
                                                        </div>
                                                        <div className="col-md-6">
                                                            <label for="endDate" className="col-form-label">To Date: (Min-Max: {moment(minDate).format('DD-MM-YYYY')} - {moment(maxDate).format('DD-MM-YYYY')})</label>
                                                            <input type="date" className="form-control" id="endDate" name='endDate' onChange={handleInputChange} value={sectionData.endDate} min={minDate} max={maxDate} />
                                                            <ErrorMessage errors={errors} field="endDate" />
                                                        </div></>}
                                            </div>

                                            <div className="row">
                                                <div className="col-md-6 d-flex justify-content-end mt-3">
                                                    <button className="btn btn-primary" type="button" onClick={getTeacherPerformance}>Show</button>
                                                </div>
                                                <div className="col-md-6 mt-3">
                                                    <button className="btn btn-danger delete_icon delete-icon" type="reset" onClick={handleInputReset}>Reset</button>
                                                </div>

                                                <div className="d-grid gap-2 mt-2">
                                                    <div class="tab-content rounded-bottom custom_table">
                                                        <div className='search_btn_row'>
                                                            <input
                                                                className="form-control mr-sm-2"
                                                                type="Search"
                                                                placeholder="Search"
                                                                value={searchValue}
                                                                onChange={handleEmptySearch}
                                                                onKeyDown={(event) => {
                                                                    if (event.key === 'Enter') {
                                                                        handleSearch();
                                                                    }
                                                                }}
                                                            />
                                                            <button className="btn btn-outline-primary" onClick={handleSearch}><i class="fa fa-search" aria-hidden="true"></i></button>
                                                        </div>
                                                        {teacherList && teacherList.length > 0 && <GeneratePdf id="teacherPerformance" title={({ data: teacherList, instituteMasterDetails, sectionMasterDetails })} />}
                                                        <div id='teacherPerformance'>
                                                            <div class="tab-pane p-3 active preview" role="tabpanel" id="preview-1000">
                                                                <table class="table">
                                                                    <thead>
                                                                        <tr>
                                                                            <th scope="col" className='sl_no'>Sr. No.</th>
                                                                            <th scope="col">Teacher Name</th>
                                                                            <th scope="col">Class</th>
                                                                            <th scope="col">Division</th>
                                                                            <th scope="col">Subject</th>
                                                                            <th scope="col">
                                                                                Daily Teaching Count
                                                                                <SortIcon sortState={teachingCountSort} setSortState={setTeachingCountSort} setData={setTeacherList} setCurrPage={setCurrPage} sortingPath={"dailyTeachingCount"} />
                                                                            </th>
                                                                            <th scope="col">
                                                                                Assignment Count
                                                                                <SortIcon sortState={assignmentCountSort} setSortState={setAssignmentCountSort} setData={setTeacherList} setCurrPage={setCurrPage} sortingPath={"dailyAssignmentCount"} />
                                                                            </th>
                                                                            <th scope="col">
                                                                                Exam Count
                                                                                <SortIcon sortState={examCountSort} setSortState={setExamCountSort} setData={setTeacherList} setCurrPage={setCurrPage} sortingPath={"totalExamsCount"} />
                                                                            </th>
                                                                            <th scope="col">
                                                                                Completion Percentage
                                                                                <SortIcon sortState={completionPercentageSort} setSortState={setCompletionPercentageSort} setData={setTeacherList} setCurrPage={setCurrPage} sortingPath={"completionPercentage"} />
                                                                            </th>
                                                                            <th scope="col">
                                                                                Rating
                                                                                <SortIcon sortState={ratingSort} setSortState={setRatingSort} setData={setTeacherList} setCurrPage={setCurrPage} sortingPath={"ratingAverage"} />
                                                                            </th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {
                                                                            displayedData?.length > 0 ?
                                                                                displayedData?.map((teacher, index) => {
                                                                                    return (
                                                                                        <tr>
                                                                                            <th scope="row" className='sl_no'>{startIndex + index + 1}</th>
                                                                                            <td>{teacher?.teacherName}</td>
                                                                                            <td>{teacher?.teacherMappingDetails?.map(grade => grade.instituteGradeMasterDetails.gradeName).join(', ')}</td>
                                                                                            <td>{teacher?.teacherMappingDetails?.map(grade => grade.instituteDivisionMasterDetails.divisionName).join(', ')}</td>
                                                                                            <td>{teacher?.teacherMappingDetails?.map(grade => grade.instituteSubjectMasterDetails.subjectName).join(', ')}</td>
                                                                                            <td>{teacher?.dailyTeachingCount}</td>
                                                                                            <td>{teacher?.dailyAssignmentCount}</td>
                                                                                            <td>{teacher?.totalExamsCount}</td>
                                                                                            <td>{teacher?.completionPercentage}</td>
                                                                                            <td>{teacher?.ratingAverage}</td>
                                                                                        </tr>
                                                                                    )
                                                                                })
                                                                                :
                                                                                (<tr>
                                                                                    <th colspan={"100%"} style={{ textAlign: 'center' }}>No Exam Available</th>
                                                                                </tr>)
                                                                        }
                                                                    </tbody>
                                                                </table>
                                                                <Pagination currPage={currPage} setCurrPage={setCurrPage} totalPages={totalPages} itemsPerPage={itemsPerPage} setItemsPerPage={setItemsPerPage} totalItem={teacherList.length} />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <Footer />
        </>
    )
};

export default SectionTeacherPerformance;